export interface AscentLoginHeroConfig {
  eyebrow: string;
  title: string;
  accent: string;
  body: string;
  cta: string;
  detail: string;
  cardHeading: string;
}

export interface AscentLoginNotice {
  id?: number;
  kind: 'release' | 'maintenance' | 'update' | 'incident';
  title: string;
  body: string;
  action?: string;
  detail?: string;
  dismissible?: boolean;
}

/** Sample notices for previewing the login page in Master Admin. */
export const DEMO_LOGIN_NOTICES: Record<string, AscentLoginNotice> = {
  release: {
    kind: 'release',
    title: 'SCORM 2004 packages are now supported',
    body: 'Import courses from Articulate, iSpring and Captivate straight into your catalogue.',
    action: 'See what’s new',
    detail: 'SCORM 2004 courses can be uploaded from the module library. Existing packages are unchanged.',
    dismissible: true
  },
  maintenance: {
    kind: 'maintenance',
    title: 'Scheduled maintenance',
    body: 'Sunday 02:00–04:00 UTC. Ascent will be read-only. Exams in progress will be saved.',
    dismissible: true
  },
  update: {
    kind: 'update',
    title: 'Dangerous Goods recurrent course updated',
    body: 'Anyone already enrolled keeps their progress. New assignments use the updated content.',
    dismissible: true
  },
  incident: {
    kind: 'incident',
    title: 'Action required — update your recovery email',
    body: 'Accounts without a verified recovery email will lose self-serve password reset.',
    action: 'Details',
    detail: 'Add a recovery email from your profile so you can reset your password without an administrator.',
    dismissible: false
  }
};

export const DEMO_LOGIN_ERROR = 'The email or password you entered is incorrect.';

export const DEMO_LOGIN_RESET =
  '<b>A password reset email will be sent shortly.</b><ul><li>WAIT for it — do not click reset again.</li><li>LOOK in your spam/junk folder.</li></ul>';

export const DEFAULT_ASCENT_LOGIN_HERO: AscentLoginHeroConfig = {
  eyebrow: 'Your next chapter starts here',
  title: 'A familiar place.',
  accent: 'A clearer path forward.',
  body: 'Pick up your training, find your records and keep moving toward your next milestone.',
  cta: 'Explore your workspace',
  detail: 'Ascent brings your courses, exams and training records together. Sign in with the credentials provided by your organization.',
  cardHeading: 'Courses, exams and records. All in one place.',
};

export interface AscentLoginFormCopy {
  eyebrow: string;
  heading: string;
  intro: string;
}

export const DEFAULT_ASCENT_LOGIN_FORM: AscentLoginFormCopy = {
  eyebrow: 'WELCOME TO ASCENT',
  heading: 'Sign in to Ascent.',
  intro: 'Your training, records and next steps. All in one place.'
};

export interface AscentLoginPageCopy {
  hero: AscentLoginHeroConfig;
  form: AscentLoginFormCopy;
  /** When false, hide Google/Microsoft/org SSO on the sign-in form. Default true. */
  showSso: boolean;
}

function filled(value: unknown, fallback: string): string {
  const text = typeof value === 'string' ? value.trim() : '';
  return text === '' ? fallback : text;
}

function flag(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 0 || value === '0' || value === 'false') {
    return false;
  }
  if (value === 1 || value === '1' || value === 'true') {
    return true;
  }
  return fallback;
}

/** Empty strings keep the built-in copy. */
export function mergeLoginCopy(saved: Partial<AscentLoginPageCopy> | null | undefined): AscentLoginPageCopy {
  const incoming = saved || {};
  const heroIn = incoming.hero || ({} as AscentLoginHeroConfig);
  const formIn = incoming.form || ({} as AscentLoginFormCopy);

  return {
    hero: {
      eyebrow: filled(heroIn.eyebrow, DEFAULT_ASCENT_LOGIN_HERO.eyebrow),
      title: filled(heroIn.title, DEFAULT_ASCENT_LOGIN_HERO.title),
      accent: filled(heroIn.accent, DEFAULT_ASCENT_LOGIN_HERO.accent),
      body: filled(heroIn.body, DEFAULT_ASCENT_LOGIN_HERO.body),
      cta: filled(heroIn.cta, DEFAULT_ASCENT_LOGIN_HERO.cta),
      detail: filled(heroIn.detail, DEFAULT_ASCENT_LOGIN_HERO.detail),
      cardHeading: filled(heroIn.cardHeading, DEFAULT_ASCENT_LOGIN_HERO.cardHeading)
    },
    form: {
      eyebrow: filled(formIn.eyebrow, DEFAULT_ASCENT_LOGIN_FORM.eyebrow),
      heading: filled(formIn.heading, DEFAULT_ASCENT_LOGIN_FORM.heading),
      intro: filled(formIn.intro, DEFAULT_ASCENT_LOGIN_FORM.intro)
    },
    showSso: flag(incoming.showSso, true)
  };
}
